import type { Canvas, StaticCanvas, FabricObject } from 'fabric';
import { isDugout } from '@/config/features';
import type { DrawingJSON } from '@/types/drawing';
import rebuildLines, { type LineGroup } from './lines/rebuildLines';
import convertAssetsToFrf from './convertAssetsToFrf';
import convertAssetsToDugout, {
  remapDugoutAsset,
} from './convertAssetsToDugout';
import recolorDugoutImport from './recolorDugoutImport';
// Registers the custom 'group' class that reloads SVG-sourced groups from their sourcePath URL
// (the built-in Group can't revive the compact string-`objects` form). Must load before loadFromJSON.
import '@/utils/registerFabricGroup';

// Line render objects (path + arrowhead) are stripped and rebuilt from the 3 control circles, because
// fabric cannot restore quadratic-curve event handlers from JSON. The strip filter is case-insensitive
// because current exports serialize `type` capitalized ('Path'/'Triangle') while legacy saved drawings
// use lowercase ('path'/'triangle') — both must be stripped.
const STRIP_TYPES = new Set(['path', 'triangle', 'shotline', 'squiggleyline']);

const importJSON = (
  savedJSON: string,
  canvas: Canvas | StaticCanvas,
  isMobile: boolean,
  // The resolved theme, before the optional callback so existing callers keep their trailing argument.
  theme: string,
  callback?: () => void,
): void => {
  canvas.clear();

  const lineGroups: LineGroup[] = [];
  // The frf/fmf asset conversion follows the BUILD theme (those federations rebrand the shirt files),
  // whereas dugout is resolved per request — so the dugout branch is tested FIRST. Reversed, an frf
  // build serving a dugout request would apply the frf conversion and skip the dugout one.
  const buildTheme = process.env.NEXT_PUBLIC_THEME;
  const isDugoutTheme = isDugout(theme);

  let lineId = 1;
  let linePartCounter = 0; // 3 parts in a line

  // We needed the path (line) and triangle (arrowhead) for the export (svg). Need to remove those now
  // as we rebuild the lines based on the circles (head, middle, end) to set eventhandlers on them.
  const parsed = JSON.parse(savedJSON) as DrawingJSON;
  parsed.objects = parsed.objects.filter(
    (item) => !STRIP_TYPES.has(String(item.type).toLowerCase()),
  );

  if (isDugoutTheme) {
    parsed.objects = convertAssetsToDugout(parsed.objects);
    // The field PNG lives outside `objects[]`; remap its src too, else a legacy `/static/media/*`
    // background 404s on enliven and aborts the whole loadFromJSON (empty canvas). setField reloads
    // the real field from config afterwards — this just needs a VALID src to enliven.
    const bg = parsed.backgroundImage;
    if (bg && typeof bg.src === 'string') bg.src = remapDugoutAsset(bg.src);
  } else if (buildTheme === 'frf' || buildTheme === 'fmf') {
    parsed.objects = convertAssetsToFrf(parsed.objects);
  }

  const afterLoading = () => {
    rebuildLines(lineGroups, canvas, isMobile);
    // Dugout's dataless groups reload the RAW recolourable SVG, so re-apply each shape's team colour
    // (its `js-main-color` element) from the serialized `color` — see recolorDugoutImport.
    if (isDugoutTheme) recolorDugoutImport(canvas, parsed.iconType);
    if (callback) callback();
  };

  // loadFromJSON returns a Promise (there is no callback arg); the reviver is still the 2nd arg and is
  // called as (serializedObject, instance).
  void canvas
    .loadFromJSON(
      parsed,
      (_data: Record<string, unknown>, instance: unknown) => {
        // `instance` is the enlivened Fabric object; Fabric types the reviver's 2nd arg as a broad
        // generic, so narrow it here (custom keys like `variation`/`name` survive enliven — see probe log).
        const o = instance as FabricObject;
        if (!o.variation) return;
        if (o.variation === 'field-text') return;

        // generate an array of objects, each containing coordinate data about a line
        if (o.variation.includes('line')) {
          lineGroups[lineId] = {
            ...lineGroups[lineId],
            [o.name as string]: {
              variation: o.variation,
              top: o.top,
              left: o.left,
              stroke: o.stroke,
            },
          } as LineGroup;

          // Keeping track of lineparts, to group them and keep line id unique so lines don't get overwritten
          linePartCounter++;

          if (linePartCounter === 3) {
            linePartCounter = 0;
            lineId++;
          }
        }
      },
    )
    .then(afterLoading);
};

export default importJSON;
